import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "./api";
import { waitForQueueReady } from "./admissionPolling";

// POSTs are retried only after explicit admission permission. Only read-only
// queue polling tolerates bounded transient errors; ambiguous POSTs never replay.
export async function postWithAdmission(path, body, { signal, onQueue, ...config } = {}) {
  if (!/^\/auth\/(login|mobile\/login|staff-mobile\/login)$/.test(path) && !/^\/live\/[^/]+\/join$/.test(path)) {
    throw new Error("Fila permitida somente para entradas");
  }
  const ownController = new AbortController();
  const effectiveSignal = signal || ownController.signal;
  let ticket = "";
  try {
    while (!effectiveSignal.aborted) {
      const response = await api.post(path, body, { ...config, signal: effectiveSignal, headers: {
        ...config.headers, "X-Admission-Support": "1", ...(ticket ? { "X-Admission-Ticket": ticket } : {}),
      } });
      if (response.status !== 202 || !["admission_queued", "admission_ready"].includes(response.data?.status)) return response;
      ticket = response.data.ticket;
      const state = await waitForQueueReady({ get: (url, options) => api.get(url, options),
        ticket, initialState: response.data, signal: effectiveSignal, onQueue });
      onQueue?.({ ...state, message: "Sua vez chegou. Validando a entrada…" });
    }
    throw new DOMException("Entrada cancelada", "AbortError");
  } finally {
    // A failed/cancelled browser must not keep a place indefinitely. The
    // server also expires inactive tickets if the network is unavailable.
    if (ticket) api.post("/admission/cancel", {}, { headers: { "X-Admission-Ticket": ticket }, timeout: 3000 }).catch(() => {});
    onQueue?.(null);
  }
}

export function useAdmissionPost() {
  const [queue, setQueue] = useState(null);
  const active = useRef(null);
  useEffect(() => () => active.current?.abort(), []);
  const cancel = useCallback(() => active.current?.abort(), []);
  const post = useCallback(async (path, body, config = {}) => {
    if (active.current) throw new Error("A entrada já está em andamento");
    const controller = new AbortController();
    active.current = controller;
    try { return await postWithAdmission(path, body, { ...config, signal: controller.signal, onQueue: setQueue }); }
    finally { active.current = null; }
  }, []);
  return { post, queue, cancel };
}

export function usePreloginAdmission(enabled = true) {
  const [ready, setReady] = useState(!enabled);
  const [queue, setQueue] = useState(null);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const active = useRef(null);

  useEffect(() => {
    if (!enabled) {
      setReady(true);
      setQueue(null);
      setError("");
      return undefined;
    }
    const controller = new AbortController();
    active.current = controller;
    let ticket = "";
    setReady(false);
    setQueue(null);
    setError("");

    const run = async () => {
      try {
        while (!controller.signal.aborted) {
          const response = await api.post("/admission/prelogin", {}, {
            signal: controller.signal,
            headers: ticket ? { "X-Admission-Ticket": ticket } : {},
          });
          if (response.status === 200 && response.data?.status === "prelogin_admitted") {
            setReady(true);
            setQueue(null);
            return;
          }
          if (response.status !== 202 || !["admission_queued", "admission_ready"].includes(response.data?.status)) {
            throw new Error("Resposta inválida da antessala");
          }
          ticket = response.data.ticket;
          await waitForQueueReady({
            get: (url, options) => api.get(url, options),
            ticket,
            initialState: response.data,
            signal: controller.signal,
            onQueue: setQueue,
          });
          setQueue((current) => ({ ...(current || {}), position: 0, message: "Sua vez chegou. Liberando o acesso…" }));
          // The read-only poll only announces the turn. This credential-free
          // POST atomically consumes it before the login form is rendered.
        }
      } catch (requestError) {
        if (!admissionCancelled(requestError)) {
          setError(requestError?.response?.data?.detail || requestError?.message || "Não foi possível entrar na fila.");
        }
      } finally {
        if (ticket) {
          api.post("/admission/cancel", {}, {
            headers: { "X-Admission-Ticket": ticket }, timeout: 3000,
          }).catch(() => {});
        }
        if (active.current === controller) active.current = null;
      }
    };
    void run();
    return () => controller.abort();
  }, [attempt, enabled]);

  const cancel = useCallback(() => {
    active.current?.abort();
    setQueue(null);
    setError("Você saiu da fila de entrada.");
  }, []);
  const retry = useCallback(() => setAttempt((value) => value + 1), []);
  return { ready, queue, error, cancel, retry };
}

export function admissionCancelled(error) {
  return error?.name === "AbortError" || error?.code === "ERR_CANCELED";
}
